"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * MapFeatureTools — マップ編集画面の地物（街・ランドマーク・街道）の部分。
 *
 * 重ね描きの canvas・一覧・形の編集・md のパネルを 1 つのフックにまとめ、
 * ProvincePaintEditor から呼ぶ。データの形は詳細設計の地物が正本。
 */

const MAP_W = 2400;
const MAP_H = 1600;

type Pixel = [number, number];
type Run = [number, number, number];
type FeatureKind = "town" | "landmark" | "road";
type FeatureCategory = "towns" | "natural" | "ruins" | "lairs" | "roads";
type FeatureShape = "point" | "line" | "area";

interface FeatureTypeEntry {
  category: FeatureCategory;
  type: string;
  label: string;
  shape: FeatureShape;
}

interface FeatureData {
  slug: string;
  name: string;
  kind: FeatureKind;
  category: FeatureCategory;
  type: string;
  shape: FeatureShape;
  pixels: Pixel[];
  lines: Pixel[][];
  runs: Run[];
  /** 発見済みか。偽は未発見 */
  discovered: boolean;
}

interface FeatureView extends FeatureData {
  /** 形を囲む箱。x0・y0・x1・y1。形が空なら x1 < x0 */
  box: [number, number, number, number];
}

interface UnplacedEntry {
  slug: string;
  name: string;
  category: FeatureCategory;
}

interface FeaturesResponse {
  count: number;
  types: FeatureTypeEntry[];
  features: FeatureData[];
  unplaced: UnplacedEntry[];
}

interface View {
  zoom: number;
  panX: number;
  panY: number;
}

/** 編集中の地物。形そのものは ref が持つ */
interface Draft {
  /** 既存の地物を直すときのスラッグ。新しく作るときは null */
  slug: string | null;
  /** 形を持たない既存フォルダへ形を付けるとき true */
  attach: boolean;
  /** 新しく作るときのスラッグ。空なら連番を発行してもらう */
  newSlug: string;
  name: string;
  category: FeatureCategory;
  type: string;
  shape: FeatureShape;
  discovered: boolean;
}

interface Geometry {
  pixels: Pixel[];
  lines: Pixel[][];
}

const CATEGORY_LABEL: Record<FeatureCategory, string> = {
  towns: "街",
  natural: "自然",
  ruins: "遺構",
  lairs: "巣",
  roads: "街道",
};
const CATEGORIES: FeatureCategory[] = ["towns", "natural", "ruins", "lairs", "roads"];

const SHAPE_LABEL: Record<FeatureShape, string> = { point: "点", line: "線", area: "面" };

/** 一覧に出す件数の上限 */
const LIST_MAX = 200;
/** 画面の上でこの距離までに在る地物を拾う */
const PICK_PX = 6;

/** 面の色。重なったときは後ろのものが上に来る */
const AREA_COLOR: Record<string, [number, number, number]> = {
  "great-forest": [31, 107, 53],
  forest: [60, 143, 74],
  mountains: [138, 111, 77],
  mountain: [160, 133, 96],
  volcano: [192, 69, 47],
  lake: [63, 143, 208],
};
const AREA_ORDER = ["great-forest", "forest", "mountains", "mountain", "volcano", "lake"];

const RIVER_COLOR = "#4aa8ff";
const ROAD_COLOR = "#e8c84a";
const DRAFT_COLOR = "#ff4fd8";
const SELECT_COLOR = "#ffffff";

/** 街の点の半径。画面のピクセル */
const TOWN_RADIUS: Record<string, number> = {
  capital: 6,
  "state-capital": 5,
  city: 4,
  town: 3,
  village: 2,
};
const TOWN_FILL: Record<string, string> = {
  capital: "#ff5a4a",
  "state-capital": "#ffb04a",
  city: "#ffffff",
  town: "#e6e6e6",
  village: "#c8c8c8",
};
/** 点のランドマークの色。置き場で分ける */
const POINT_FILL: Record<FeatureCategory, string> = {
  towns: "#ffffff",
  natural: "#7fd08a",
  ruins: "#c59cff",
  lairs: "#ff6a5a",
  roads: ROAD_COLOR,
};
/** 未発見の点の色 */
const UNDISCOVERED_FILL = "#4a4a52";

function boxOf(f: FeatureData): [number, number, number, number] {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const add = (x: number, y: number) => {
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  };
  for (const [x, y] of f.pixels) add(x, y);
  for (const line of f.lines) for (const [x, y] of line) add(x, y);
  for (const [y, a, b] of f.runs) {
    add(a, y);
    add(b, y);
  }
  if (x0 === Infinity) return [0, 0, -1, -1];
  return [x0, y0, x1, y1];
}

/** 応答の JSON を読む。JSON でない応答（サーバーの落ちたときの HTML など）は空の箱にする */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function readJson(res: Response): Promise<Record<string, any>> {
  try {
    const json = await res.json();
    return json && typeof json === "object" ? json : {};
  } catch {
    return {};
  }
}

function toView(f: FeatureData): FeatureView {
  return { ...f, discovered: f.discovered !== false, box: boxOf(f) };
}

/** 点と線分の距離 */
function segmentDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len = dx * dx + dy * dy;
  let t = len === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / len;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

/** 地図のピクセルから地物までの距離。箱の外で reach より遠ければ Infinity */
function distanceTo(f: FeatureView, x: number, y: number, reach: number): number {
  const [x0, y0, x1, y1] = f.box;
  if (x1 < x0) return Infinity;
  if (x < x0 - reach || x > x1 + reach || y < y0 - reach || y > y1 + reach) return Infinity;
  let best = Infinity;
  if (f.shape === "point") {
    for (const [px, py] of f.pixels) best = Math.min(best, Math.hypot(px - x, py - y));
  } else if (f.shape === "line") {
    for (const line of f.lines) {
      if (line.length === 1) best = Math.min(best, Math.hypot(line[0][0] - x, line[0][1] - y));
      for (let i = 1; i < line.length; i++) {
        best = Math.min(
          best,
          segmentDistance(x, y, line[i - 1][0], line[i - 1][1], line[i][0], line[i][1])
        );
      }
    }
  } else {
    for (const [ry, a, b] of f.runs) {
      const dy = Math.abs(ry - y);
      if (dy > reach) continue;
      const dx = x < a ? a - x : x > b ? x - b : 0;
      best = Math.min(best, Math.hypot(dx, dy));
      if (best === 0) break;
    }
  }
  return best;
}

export interface MapFeatureToolsOptions {
  ready: boolean;
  /** 地物モードが表に出ているか */
  active: boolean;
  viewStateRef: React.MutableRefObject<View>;
  sizeRef: React.MutableRefObject<{ w: number; h: number }>;
  setView: React.Dispatch<React.SetStateAction<View>>;
  toMapXY: (clientX: number, clientY: number) => { x: number; y: number } | null;
  setStatus: (text: string) => void;
  ask: (message: string, options?: { ok?: string; cancel?: string; danger?: boolean }) => Promise<boolean>;
  btn: (on: boolean) => React.CSSProperties;
  label: React.CSSProperties;
}

export function useMapFeatureTools(opts: MapFeatureToolsOptions) {
  const { ready, active, viewStateRef, sizeRef, setView, toMapXY, setStatus, ask, btn, label } = opts;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  /** 地物の canvas だけを次のフレームで描き直す。色面と境界線は触らない */
  const drawRef = useRef<() => void>(() => {});
  const overlayFrameRef = useRef(0);
  const requestOverlay = useCallback(() => {
    if (overlayFrameRef.current) return;
    overlayFrameRef.current = requestAnimationFrame(() => {
      overlayFrameRef.current = 0;
      drawRef.current();
    });
  }, []);
  const featuresRef = useRef<Map<string, FeatureView>>(new Map());
  /** 面をまとめて焼いた原寸の絵。表示のときに引き伸ばす */
  const areaFaceRef = useRef<HTMLCanvasElement | null>(null);

  const [types, setTypes] = useState<FeatureTypeEntry[]>([]);
  const [unplaced, setUnplaced] = useState<UnplacedEntry[]>([]);
  /** 地物の中身が変わるたびに進める。一覧の作り直しに使う */
  const [version, setVersion] = useState(0);

  const [showTown, setShowTown] = useState(true);
  const [showLandmark, setShowLandmark] = useState(true);
  const [showRoad, setShowRoad] = useState(true);
  const [showUndiscovered, setShowUndiscovered] = useState(true);
  const showRef = useRef({ town: true, landmark: true, road: true, undiscovered: true });
  showRef.current = { town: showTown, landmark: showLandmark, road: showRoad, undiscovered: showUndiscovered };

  const [category, setCategory] = useState<FeatureCategory | "all">("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selected;

  // 新規の入力
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newCategory, setNewCategory] = useState<FeatureCategory>("towns");
  const [newType, setNewType] = useState("village");
  const [newUndiscovered, setNewUndiscovered] = useState(false);
  const [showUnplaced, setShowUnplaced] = useState(false);

  // 編集中の形
  const [draft, setDraft] = useState<Draft | null>(null);
  const draftRef = useRef<Draft | null>(null);
  draftRef.current = draft;
  const geomRef = useRef<Geometry>({ pixels: [], lines: [] });
  const maskRef = useRef<Uint8Array | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const undoRef = useRef<{ pixels: Pixel[]; lines: Pixel[][]; mask: Uint8Array | null } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  /** 形の見出しの数字を出し直すために進める */
  const [, setGeomTick] = useState(0);
  const [saving, setSaving] = useState(false);

  /** 形を触る道具。move は地図を動かす */
  const [tool, setTool] = useState<"edit" | "move">("edit");
  const toolRef = useRef(tool);
  toolRef.current = tool;
  const [eraser, setEraser] = useState(false);
  const eraserRef = useRef(false);
  eraserRef.current = eraser;
  const [radius, setRadius] = useState(3);
  const radiusRef = useRef(3);
  radiusRef.current = radius;

  const dragRef = useRef<{
    kind: "pan" | "line" | "area" | "point";
    px: number;
    py: number;
    panX: number;
    panY: number;
    moved: boolean;
    last: { x: number; y: number } | null;
  } | null>(null);
  const cursorRef = useRef<{ x: number; y: number } | null>(null);

  // md のパネル
  const [docSlug, setDocSlug] = useState<string | null>(null);
  const [docText, setDocText] = useState("");
  const [docStatus, setDocStatus] = useState("");

  const typeLabel = useCallback(
    (cat: string, type: string) => types.find((t) => t.category === cat && t.type === type)?.label ?? type,
    [types]
  );

  // ── 面の絵を焼く ──
  const rebuildAreaFace = useCallback(() => {
    let face = areaFaceRef.current;
    if (!face) {
      face = document.createElement("canvas");
      face.width = MAP_W;
      face.height = MAP_H;
      areaFaceRef.current = face;
    }
    const ctx = face.getContext("2d")!;
    const image = ctx.createImageData(MAP_W, MAP_H);
    const data = image.data;
    // 編集中のものは別に描くので外す
    const skip = draftRef.current?.slug ?? null;
    const withUndiscovered = showRef.current.undiscovered;
    const areas = Array.from(featuresRef.current.values())
      .filter((f) => f.shape === "area" && f.slug !== skip && (withUndiscovered || f.discovered))
      .sort((a, b) => AREA_ORDER.indexOf(a.type) - AREA_ORDER.indexOf(b.type));
    for (const f of areas) {
      const rgb = AREA_COLOR[f.type] ?? [170, 170, 170];
      for (const [y, a, b] of f.runs) {
        if (y < 0 || y >= MAP_H) continue;
        const from = Math.max(0, a);
        const to = Math.min(MAP_W - 1, b);
        let o = (y * MAP_W + from) * 4;
        for (let x = from; x <= to; x++) {
          data[o] = rgb[0];
          data[o + 1] = rgb[1];
          data[o + 2] = rgb[2];
          data[o + 3] = 255;
          o += 4;
        }
      }
    }
    ctx.putImageData(image, 0, 0);
  }, []);

  // ── 読み込み ──
  const load = useCallback(async () => {
    const res = await fetch("/api/province-map/features");
    if (!res.ok) throw new Error("地物を読めない");
    const json = (await res.json()) as FeaturesResponse;
    const map = new Map<string, FeatureView>();
    for (const f of json.features) map.set(f.slug, toView(f));
    featuresRef.current = map;
    setTypes(json.types);
    setUnplaced(json.unplaced);
    setVersion((v) => v + 1);
    rebuildAreaFace();
    requestOverlay();
    return map.size;
  }, [rebuildAreaFace, requestOverlay]);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    load().catch((e) => {
      if (!cancelled) setStatus(`失敗: ${(e as Error).message}`);
    });
    return () => {
      cancelled = true;
    };
  }, [ready, load, setStatus]);

  // ── 描く ──
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { w, h } = sizeRef.current;
    if (w === 0 || h === 0) return;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, w, h);

    const { zoom, panX, panY } = viewStateRef.current;
    const left = w / 2 + panX - (MAP_W * zoom) / 2;
    const top = h / 2 + panY - (MAP_H * zoom) / 2;
    // ピクセルの中心を画面へ
    const sx = (x: number) => left + (x + 0.5) * zoom;
    const sy = (y: number) => top + (y + 0.5) * zoom;
    // 画面に入る地図の範囲
    const vx0 = -left / zoom - 2;
    const vy0 = -top / zoom - 2;
    const vx1 = (w - left) / zoom + 2;
    const vy1 = (h - top) / zoom + 2;
    const visible = (f: FeatureView) =>
      f.box[2] >= f.box[0] && f.box[2] >= vx0 && f.box[0] <= vx1 && f.box[3] >= vy0 && f.box[1] <= vy1;

    const show = showRef.current;
    const d = draftRef.current;
    const skip = d?.slug ?? null;
    const sel = selectedRef.current;

    // 面
    if (show.landmark && areaFaceRef.current) {
      ctx.imageSmoothingEnabled = false;
      ctx.globalAlpha = 0.45;
      ctx.drawImage(areaFaceRef.current, left, top, MAP_W * zoom, MAP_H * zoom);
      ctx.globalAlpha = 1;
    }

    // 線。川と街道をそれぞれ 1 本の道筋にまとめて引く
    const rivers: FeatureView[] = [];
    const greatRivers: FeatureView[] = [];
    const roads: FeatureView[] = [];
    const towns: FeatureView[] = [];
    const points: FeatureView[] = [];
    const undiscovered: FeatureView[] = [];
    for (const f of featuresRef.current.values()) {
      if (f.slug === skip || !visible(f)) continue;
      if (!f.discovered && !show.undiscovered) continue;
      if (f.kind === "road") {
        if (show.road) roads.push(f);
      } else if (f.kind === "town") {
        if (show.town) towns.push(f);
      } else if (show.landmark) {
        if (f.shape === "line") (f.type === "great-river" ? greatRivers : rivers).push(f);
        else if (f.shape === "point") (f.discovered ? points : undiscovered).push(f);
      }
    }

    const trace = (lines: Pixel[][]) => {
      for (const line of lines) {
        if (line.length === 0) continue;
        ctx.moveTo(sx(line[0][0]), sy(line[0][1]));
        for (let i = 1; i < line.length; i++) ctx.lineTo(sx(line[i][0]), sy(line[i][1]));
      }
    };
    const strokeAll = (list: FeatureView[], color: string, width: number) => {
      if (list.length === 0) return;
      ctx.beginPath();
      for (const f of list) trace(f.lines);
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.stroke();
    };
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    const lineScale = Math.max(1, Math.min(3, zoom * 0.8));
    strokeAll(roads, ROAD_COLOR, lineScale);
    strokeAll(rivers, RIVER_COLOR, lineScale * 1.2);
    strokeAll(greatRivers, RIVER_COLOR, lineScale * 2.2);

    // 点のランドマーク。四角で描く
    const pointScale = Math.max(0.7, Math.min(2, Math.sqrt(zoom)));
    const outline = zoom >= 0.5;
    // 未発見の点は暗い色で先に描き、発見済みのものを上にする
    for (const cat of [null, "natural", "ruins", "lairs"] as Array<FeatureCategory | null>) {
      const r = 2.5 * pointScale;
      ctx.beginPath();
      for (const f of cat === null ? undiscovered : points) {
        if (cat !== null && f.category !== cat) continue;
        for (const [x, y] of f.pixels) ctx.rect(sx(x) - r, sy(y) - r, r * 2, r * 2);
      }
      ctx.fillStyle = cat === null ? UNDISCOVERED_FILL : POINT_FILL[cat];
      ctx.fill();
      if (outline) {
        ctx.strokeStyle = "rgba(0,0,0,0.8)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // 街。丸で描く。小さい種別から先に描いて大きいものを上にする
    for (const type of ["village", "town", "city", "state-capital", "capital"]) {
      const r = (TOWN_RADIUS[type] ?? 2) * pointScale;
      for (const found of [false, true]) {
        ctx.beginPath();
        let any = false;
        for (const f of towns) {
          if (f.type !== type || f.discovered !== found) continue;
          for (const [x, y] of f.pixels) {
            ctx.moveTo(sx(x) + r, sy(y));
            ctx.arc(sx(x), sy(y), r, 0, Math.PI * 2);
            any = true;
          }
        }
        if (!any) continue;
        ctx.fillStyle = found ? (TOWN_FILL[type] ?? "#ffffff") : UNDISCOVERED_FILL;
        ctx.fill();
        if (outline) {
          ctx.strokeStyle = "rgba(0,0,0,0.85)";
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
    }

    const text = (s: string, x: number, y: number) => {
      ctx.font = "12px sans-serif";
      ctx.textBaseline = "middle";
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(0,0,0,0.85)";
      ctx.strokeText(s, x, y);
      ctx.fillStyle = "#ffffff";
      ctx.fillText(s, x, y);
    };

    // 街の名
    if (zoom >= 3) {
      for (const f of towns) {
        if (!f.name || f.pixels.length === 0) continue;
        const r = (TOWN_RADIUS[f.type] ?? 2) * pointScale;
        text(f.name, sx(f.pixels[0][0]) + r + 4, sy(f.pixels[0][1]));
      }
    }

    // 選んでいる地物
    const picked = sel && sel !== skip ? featuresRef.current.get(sel) : undefined;
    if (picked && visible(picked)) {
      if (picked.shape === "point") {
        ctx.beginPath();
        for (const [x, y] of picked.pixels) {
          ctx.moveTo(sx(x) + 10, sy(y));
          ctx.arc(sx(x), sy(y), 10, 0, Math.PI * 2);
        }
        ctx.strokeStyle = SELECT_COLOR;
        ctx.lineWidth = 2;
        ctx.stroke();
      } else if (picked.shape === "line") {
        ctx.beginPath();
        trace(picked.lines);
        ctx.strokeStyle = SELECT_COLOR;
        ctx.lineWidth = lineScale * 2.2 + 3;
        ctx.stroke();
        ctx.beginPath();
        trace(picked.lines);
        ctx.strokeStyle = picked.kind === "road" ? ROAD_COLOR : RIVER_COLOR;
        ctx.lineWidth = lineScale * 1.6;
        ctx.stroke();
      } else {
        ctx.fillStyle = "rgba(255,255,255,0.45)";
        for (const [y, a, b] of picked.runs) {
          if (y < vy0 || y > vy1) continue;
          ctx.fillRect(left + a * zoom, top + y * zoom, (b - a + 1) * zoom, zoom);
        }
      }
      const [bx0, by0, bx1] = picked.box;
      if (picked.shape !== "point" || zoom < 3 || picked.kind !== "town") {
        text(picked.name || picked.slug, sx((bx0 + bx1) / 2) + 12, sy(by0) - 10);
      }
    }

    // 編集中の形
    if (d) {
      const g = geomRef.current;
      if (d.shape === "area" && maskCanvasRef.current) {
        ctx.imageSmoothingEnabled = false;
        ctx.globalAlpha = 0.55;
        ctx.drawImage(maskCanvasRef.current, left, top, MAP_W * zoom, MAP_H * zoom);
        ctx.globalAlpha = 1;
      }
      if (d.shape === "line") {
        ctx.beginPath();
        trace(g.lines);
        ctx.strokeStyle = DRAFT_COLOR;
        ctx.lineWidth = Math.max(2, lineScale * 1.6);
        ctx.stroke();
        if (zoom >= 1) {
          ctx.fillStyle = "#ffffff";
          for (const line of g.lines) {
            for (const [x, y] of line) ctx.fillRect(sx(x) - 2, sy(y) - 2, 4, 4);
          }
        }
        const lastLine = g.lines[g.lines.length - 1];
        const tail = lastLine?.[lastLine.length - 1];
        if (tail) {
          ctx.beginPath();
          ctx.arc(sx(tail[0]), sy(tail[1]), 6, 0, Math.PI * 2);
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }
      if (d.shape === "point") {
        for (const [x, y] of g.pixels) {
          ctx.beginPath();
          ctx.arc(sx(x), sy(y), 7, 0, Math.PI * 2);
          ctx.fillStyle = DRAFT_COLOR;
          ctx.fill();
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(sx(x) - 12, sy(y));
          ctx.lineTo(sx(x) + 12, sy(y));
          ctx.moveTo(sx(x), sy(y) - 12);
          ctx.lineTo(sx(x), sy(y) + 12);
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
      // 筆の輪
      const c = cursorRef.current;
      if (d.shape === "area" && toolRef.current === "edit" && c) {
        ctx.beginPath();
        ctx.arc(sx(c.x), sy(c.y), (radiusRef.current + 0.5) * zoom, 0, Math.PI * 2);
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
  }, [sizeRef, viewStateRef]);
  drawRef.current = draw;

  useEffect(() => {
    requestOverlay();
  }, [showTown, showLandmark, showRoad, selected, draft, tool, requestOverlay]);

  // 未発見の入/切は面の絵にも効く
  const undiscoveredShownRef = useRef(showUndiscovered);
  useEffect(() => {
    if (undiscoveredShownRef.current === showUndiscovered) return;
    undiscoveredShownRef.current = showUndiscovered;
    rebuildAreaFace();
    requestOverlay();
  }, [showUndiscovered, rebuildAreaFace, requestOverlay]);

  // ── 近くの地物 ──
  const nearby = useCallback(
    (x: number, y: number): FeatureView[] => {
      const zoom = viewStateRef.current.zoom;
      const reach = Math.max(1, PICK_PX / zoom);
      const show = showRef.current;
      const hits: Array<{ f: FeatureView; d: number }> = [];
      for (const f of featuresRef.current.values()) {
        if (f.kind === "town" ? !show.town : f.kind === "road" ? !show.road : !show.landmark) continue;
        if (!f.discovered && !show.undiscovered) continue;
        const d = distanceTo(f, x, y, reach);
        if (d <= reach) hits.push({ f, d });
      }
      // 点を先に、近い順に
      const rank = (f: FeatureView) => (f.shape === "point" ? 0 : f.shape === "line" ? 1 : 2);
      hits.sort((a, b) => rank(a.f) - rank(b.f) || a.d - b.d);
      return hits.map((h) => h.f);
    },
    [viewStateRef]
  );

  /** ホバー表示に足す文字 */
  const namesNear = useCallback(
    (x: number, y: number): string => {
      const hits = nearby(x, y);
      if (hits.length === 0) return "";
      const names = hits.slice(0, 4).map((f) => f.name || f.slug);
      return names.join("・") + (hits.length > 4 ? ` ほか ${hits.length - 4}` : "");
    },
    [nearby]
  );

  // ── 地図を動かす ──
  const jumpTo = useCallback(
    (f: FeatureView) => {
      const [x0, y0, x1, y1] = f.box;
      if (x1 < x0) return;
      const { w, h } = sizeRef.current;
      const cx = (x0 + x1 + 1) / 2;
      const cy = (y0 + y1 + 1) / 2;
      setView((v) => {
        let zoom = v.zoom;
        if (f.shape === "point") {
          zoom = Math.max(v.zoom, 3);
        } else {
          const fit = Math.min(w / (x1 - x0 + 40), h / (y1 - y0 + 40)) * 0.8;
          zoom = Math.min(8, Math.max(0.4, fit));
        }
        return {
          zoom,
          panX: (MAP_W * zoom) / 2 - cx * zoom,
          panY: (MAP_H * zoom) / 2 - cy * zoom,
        };
      });
    },
    [setView, sizeRef]
  );

  const select = useCallback(
    (slug: string | null, jump: boolean) => {
      setSelected(slug);
      setCreating(false);
      if (slug && jump) {
        const f = featuresRef.current.get(slug);
        if (f) jumpTo(f);
      }
    },
    [jumpTo]
  );

  // ── 形の編集 ──
  const ensureMask = useCallback((runs: Run[]) => {
    const mask = new Uint8Array(MAP_W * MAP_H);
    let canvas = maskCanvasRef.current;
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.width = MAP_W;
      canvas.height = MAP_H;
      maskCanvasRef.current = canvas;
    }
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, MAP_W, MAP_H);
    ctx.fillStyle = DRAFT_COLOR;
    for (const [y, a, b] of runs) {
      if (y < 0 || y >= MAP_H) continue;
      const from = Math.max(0, a);
      const to = Math.min(MAP_W - 1, b);
      if (to < from) continue;
      mask.fill(1, y * MAP_W + from, y * MAP_W + to + 1);
      ctx.fillRect(from, y, to - from + 1, 1);
    }
    maskRef.current = mask;
  }, []);

  /** 塗りの面を run の並びへ直す */
  const maskToRuns = useCallback((mask: Uint8Array): Run[] => {
    const runs: Run[] = [];
    for (let y = 0; y < MAP_H; y++) {
      const base = y * MAP_W;
      let x = 0;
      while (x < MAP_W) {
        if (mask[base + x] === 0) {
          x++;
          continue;
        }
        const start = x;
        while (x < MAP_W && mask[base + x] === 1) x++;
        runs.push([y, start, x - 1]);
      }
    }
    return runs;
  }, []);

  /** 面の絵を塗りの面から描き直す */
  const repaintMask = useCallback(() => {
    const mask = maskRef.current;
    const canvas = maskCanvasRef.current;
    if (!mask || !canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, MAP_W, MAP_H);
    ctx.fillStyle = DRAFT_COLOR;
    for (const [y, a, b] of maskToRuns(mask)) ctx.fillRect(a, y, b - a + 1, 1);
  }, [maskToRuns]);

  const beginDraft = useCallback(
    (next: Draft, source: FeatureData | null) => {
      geomRef.current = {
        pixels: source && next.shape === "point" ? source.pixels.map((p) => [p[0], p[1]] as Pixel) : [],
        lines:
          source && next.shape === "line"
            ? source.lines.map((line) => line.map((p) => [p[0], p[1]] as Pixel))
            : [],
      };
      if (next.shape === "area") ensureMask(source && source.shape === "area" ? source.runs : []);
      else maskRef.current = null;
      const wasArea = source?.shape === "area";
      undoRef.current = null;
      draftRef.current = next;
      setCanUndo(false);
      setDirty(false);
      setEraser(false);
      setTool("edit");
      setDraft(next);
      setGeomTick((t) => t + 1);
      // 面を直すときは、焼いた絵から自分を外す
      if (wasArea) rebuildAreaFace();
      requestOverlay();
    },
    [ensureMask, rebuildAreaFace, requestOverlay]
  );

  const endDraft = useCallback(() => {
    draftRef.current = null;
    maskRef.current = null;
    undoRef.current = null;
    cursorRef.current = null;
    setDraft(null);
    setDirty(false);
    setCanUndo(false);
    rebuildAreaFace();
    requestOverlay();
  }, [rebuildAreaFace, requestOverlay]);

  const snapshot = useCallback(() => {
    const g = geomRef.current;
    undoRef.current = {
      pixels: g.pixels.map((p) => [p[0], p[1]] as Pixel),
      lines: g.lines.map((line) => line.map((p) => [p[0], p[1]] as Pixel)),
      mask: maskRef.current ? maskRef.current.slice() : null,
    };
    setCanUndo(true);
  }, []);

  const undo = useCallback(() => {
    const u = undoRef.current;
    if (!u) return;
    geomRef.current = { pixels: u.pixels, lines: u.lines };
    if (u.mask) {
      maskRef.current = u.mask;
      repaintMask();
    }
    undoRef.current = null;
    setCanUndo(false);
    setDirty(true);
    setGeomTick((t) => t + 1);
    requestOverlay();
  }, [repaintMask, requestOverlay]);

  /** 筆を 1 回押す。半径は地図のピクセル */
  const stamp = useCallback((x: number, y: number) => {
    const mask = maskRef.current;
    const canvas = maskCanvasRef.current;
    if (!mask || !canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = DRAFT_COLOR;
    const r = radiusRef.current;
    const erase = eraserRef.current;
    for (let dy = -r; dy <= r; dy++) {
      const yy = y + dy;
      if (yy < 0 || yy >= MAP_H) continue;
      const half = Math.floor(Math.sqrt(r * r - dy * dy));
      const from = Math.max(0, x - half);
      const to = Math.min(MAP_W - 1, x + half);
      if (to < from) continue;
      mask.fill(erase ? 0 : 1, yy * MAP_W + from, yy * MAP_W + to + 1);
      if (erase) ctx.clearRect(from, yy, to - from + 1, 1);
      else ctx.fillRect(from, yy, to - from + 1, 1);
    }
  }, []);

  /** 前の位置から今の位置まで、隙間が空かないように筆を押していく */
  const strokeTo = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }) => {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const n = Math.max(Math.abs(dx), Math.abs(dy));
      const step = Math.max(1, Math.floor(radiusRef.current / 2));
      for (let i = step; i < n; i += step) {
        stamp(Math.round(from.x + (dx * i) / n), Math.round(from.y + (dy * i) / n));
      }
      stamp(to.x, to.y);
    },
    [stamp]
  );

  const addVertex = useCallback((x: number, y: number) => {
    const g = geomRef.current;
    if (g.lines.length === 0) g.lines.push([]);
    const line = g.lines[g.lines.length - 1];
    const tail = line[line.length - 1];
    if (tail && tail[0] === x && tail[1] === y) return;
    line.push([x, y]);
  }, []);

  const breakLine = useCallback(() => {
    const g = geomRef.current;
    const lastLine = g.lines[g.lines.length - 1];
    if (!lastLine || lastLine.length === 0) return;
    snapshot();
    // 頂点が 1 つだけの線は線にならないので捨てる
    if (lastLine.length < 2) g.lines.pop();
    g.lines.push([]);
    setDirty(true);
    setGeomTick((t) => t + 1);
    requestOverlay();
  }, [requestOverlay, snapshot]);

  const popVertex = useCallback(() => {
    const g = geomRef.current;
    while (g.lines.length > 0 && g.lines[g.lines.length - 1].length === 0) g.lines.pop();
    const lastLine = g.lines[g.lines.length - 1];
    if (!lastLine) return;
    snapshot();
    lastLine.pop();
    if (lastLine.length === 0) g.lines.pop();
    setDirty(true);
    setGeomTick((t) => t + 1);
    requestOverlay();
  }, [requestOverlay, snapshot]);

  const clearShape = useCallback(() => {
    snapshot();
    geomRef.current = { pixels: [], lines: [] };
    if (maskRef.current) {
      maskRef.current = new Uint8Array(MAP_W * MAP_H);
      repaintMask();
    }
    setDirty(true);
    setGeomTick((t) => t + 1);
    requestOverlay();
  }, [repaintMask, requestOverlay, snapshot]);

  // ── ポインタ ──
  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const d = draftRef.current;
      const v = viewStateRef.current;
      const base = { px: e.clientX, py: e.clientY, panX: v.panX, panY: v.panY, moved: false };
      if (!d || toolRef.current === "move") {
        dragRef.current = { kind: "pan", ...base, last: null };
        return;
      }
      const pos = toMapXY(e.clientX, e.clientY);
      if (!pos) {
        dragRef.current = { kind: "pan", ...base, last: null };
        return;
      }
      snapshot();
      setDirty(true);
      if (d.shape === "point") {
        geomRef.current.pixels = [[pos.x, pos.y]];
        dragRef.current = { kind: "point", ...base, last: pos };
      } else if (d.shape === "line") {
        addVertex(pos.x, pos.y);
        dragRef.current = { kind: "line", ...base, last: pos };
      } else {
        stamp(pos.x, pos.y);
        dragRef.current = { kind: "area", ...base, last: pos };
      }
      setGeomTick((t) => t + 1);
      requestOverlay();
    },
    [addVertex, requestOverlay, snapshot, stamp, toMapXY, viewStateRef]
  );

  /** 掴んでいる間は true を返す */
  const onPointerMove = useCallback(
    (e: React.PointerEvent): boolean => {
      const d = draftRef.current;
      const drag = dragRef.current;
      const pos = toMapXY(e.clientX, e.clientY);
      if (d && d.shape === "area" && toolRef.current === "edit") {
        cursorRef.current = pos;
        requestOverlay();
      }
      if (!drag) return false;
      if (Math.abs(e.clientX - drag.px) > 4 || Math.abs(e.clientY - drag.py) > 4) drag.moved = true;

      if (drag.kind === "pan") {
        if (drag.moved) {
          setView((v) => ({
            ...v,
            panX: drag.panX + (e.clientX - drag.px),
            panY: drag.panY + (e.clientY - drag.py),
          }));
        }
        return true;
      }
      if (!pos) return true;
      if (drag.kind === "point") {
        geomRef.current.pixels = [[pos.x, pos.y]];
        requestOverlay();
      } else if (drag.kind === "line") {
        // 引きずっている間は、間を空けて頂点を足していく
        const gap = Math.max(3, 6 / viewStateRef.current.zoom);
        const last = drag.last!;
        if (Math.hypot(pos.x - last.x, pos.y - last.y) >= gap) {
          addVertex(pos.x, pos.y);
          drag.last = pos;
          requestOverlay();
        }
      } else if (drag.kind === "area") {
        strokeTo(drag.last!, pos);
        drag.last = pos;
        requestOverlay();
      }
      return true;
    },
    [addVertex, requestOverlay, setView, strokeTo, toMapXY, viewStateRef]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      dragRef.current = null;
      if (!drag) return;
      if (drag.kind === "pan") {
        // 動かさずに離したら、その場所の地物を選ぶ
        if (!drag.moved && !draftRef.current) {
          const pos = toMapXY(e.clientX, e.clientY);
          const hit = pos ? nearby(pos.x, pos.y)[0] : undefined;
          if (hit) select(hit.slug, false);
        }
        return;
      }
      setGeomTick((t) => t + 1);
    },
    [nearby, select, toMapXY]
  );

  const onPointerLeave = useCallback(() => {
    if (cursorRef.current) {
      cursorRef.current = null;
      requestOverlay();
    }
  }, [requestOverlay]);

  // ── 保存・削除 ──
  const post = async (body: Record<string, unknown>) => {
    const res = await fetch("/api/province-map/features", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await readJson(res);
    if (!res.ok || !json.ok) throw new Error(String(json.error ?? `HTTP ${res.status}`));
    return json;
  };

  const saveDraft = async () => {
    const d = draftRef.current;
    if (!d || saving) return;
    const g = geomRef.current;
    const shapeBody: Record<string, unknown> = {};
    if (d.shape === "point") {
      if (g.pixels.length === 0) return setStatus("点を置いてから保存する");
      shapeBody.pixels = g.pixels;
    } else if (d.shape === "line") {
      const lines = g.lines.filter((line) => line.length >= 2);
      if (lines.length === 0) return setStatus("頂点を 2 つ以上置いてから保存する");
      shapeBody.lines = lines;
    } else {
      const runs = maskRef.current ? maskToRuns(maskRef.current) : [];
      if (runs.length === 0) return setStatus("面を塗ってから保存する");
      shapeBody.runs = runs;
    }

    setSaving(true);
    setStatus("地物を保存中");
    try {
      const json = d.slug
        ? await post({ action: "update", slug: d.slug, type: d.type, discovered: d.discovered, ...shapeBody })
        : await post({
            action: "create",
            name: d.name,
            category: d.category,
            type: d.type,
            slug: d.newSlug.trim() || undefined,
            discovered: d.discovered,
            ...shapeBody,
          });
      const feature = toView(json.feature as FeatureData);
      featuresRef.current.set(feature.slug, feature);
      if (d.attach) setUnplaced((list) => list.filter((u) => u.slug !== feature.slug));
      setVersion((v) => v + 1);
      setSelected(feature.slug);
      setCreating(false);
      endDraft();
      const r = json.reflect as { provinces: number; errors: string[] };
      setStatus(
        `保存した（${feature.name || feature.slug}）。プロヴィンス ${r.provinces} 件へ反映` +
          (r.errors.length > 0 ? `、失敗 ${r.errors.length} 件` : "")
      );
    } catch (e) {
      setStatus(`保存に失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const cancelDraft = async () => {
    if (dirty && !(await ask("保存していない形がある。捨てる？", { ok: "捨てる", danger: true }))) return;
    endDraft();
  };

  const remove = async (slug: string) => {
    const f = featuresRef.current.get(slug);
    if (!f) return;
    const ok = await ask(`${f.name || f.slug}（${f.slug}）をフォルダごと消す。説明の md も消える`, {
      ok: "消す",
      danger: true,
    });
    if (!ok) return;
    setStatus("地物を削除中");
    try {
      const json = await post({ action: "delete", slug });
      featuresRef.current.delete(slug);
      if (draftRef.current?.slug === slug) endDraft();
      if (docSlug === slug) setDocSlug(null);
      setSelected(null);
      setVersion((v) => v + 1);
      rebuildAreaFace();
      requestOverlay();
      const r = json.reflect as { provinces: number; errors: string[] };
      setStatus(
        `消した（${slug}）。プロヴィンス ${r.provinces} 件へ反映` +
          (r.errors.length > 0 ? `、失敗 ${r.errors.length} 件` : "")
      );
    } catch (e) {
      setStatus(`削除に失敗: ${(e as Error).message}`);
    }
  };

  const startEdit = (f: FeatureView) => {
    beginDraft(
      {
        slug: f.slug,
        attach: false,
        newSlug: "",
        name: f.name,
        category: f.category,
        type: f.type,
        shape: f.shape,
        discovered: f.discovered,
      },
      f
    );
  };

  const startCreate = () => {
    const entry = types.find((t) => t.category === newCategory && t.type === newType);
    if (!entry) return setStatus("種別を選ぶ");
    if (newName.trim().length === 0) return setStatus("表記名を入れる");
    beginDraft(
      {
        slug: null,
        attach: false,
        newSlug: newSlug.trim(),
        name: newName.trim(),
        category: entry.category,
        type: entry.type,
        shape: entry.shape,
        discovered: !newUndiscovered,
      },
      null
    );
    setSelected(null);
  };

  const startAttach = (u: UnplacedEntry) => {
    const entry = types.find((t) => t.category === u.category);
    if (!entry) return;
    beginDraft(
      {
        slug: null,
        attach: true,
        newSlug: u.slug,
        name: u.name,
        category: u.category,
        type: entry.type,
        shape: entry.shape,
        discovered: true,
      },
      null
    );
    setSelected(null);
    setCreating(false);
  };

  /** 編集中の種別を変える。形の種類が変わるときは形を空にする */
  const changeDraftType = (type: string) => {
    const d = draftRef.current;
    if (!d) return;
    const entry = types.find((t) => t.category === d.category && t.type === type);
    if (!entry) return;
    const next = { ...d, type: entry.type, shape: entry.shape };
    if (entry.shape !== d.shape) {
      const keep = dirty;
      beginDraft(next, null);
      setDirty(keep || d.slug !== null);
    } else {
      draftRef.current = next;
      setDraft(next);
      if (d.slug) setDirty(true);
    }
  };

  const changeDraftDiscovered = (discovered: boolean) => {
    const d = draftRef.current;
    if (!d || d.discovered === discovered) return;
    const next = { ...d, discovered };
    draftRef.current = next;
    setDraft(next);
    if (d.slug) setDirty(true);
  };

  // ── md ──
  const openDoc = async (slug: string) => {
    setDocSlug(slug);
    setDocText("");
    setDocStatus("読み込み中");
    try {
      const res = await fetch(`/api/province-map/feature-doc?slug=${encodeURIComponent(slug)}`);
      const json = await readJson(res);
      if (!res.ok || typeof json.text !== "string") throw new Error(String(json.error ?? `HTTP ${res.status}`));
      setDocText(json.text);
      setDocStatus("");
    } catch (e) {
      setDocStatus(`失敗: ${(e as Error).message}`);
    }
  };

  const saveDoc = async () => {
    if (!docSlug) return;
    setDocStatus("保存中");
    try {
      const res = await fetch("/api/province-map/feature-doc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug: docSlug, text: docText }),
      });
      const json = await readJson(res);
      if (!res.ok || !json.ok) throw new Error(String(json.error ?? `HTTP ${res.status}`));
      const errors = (json.reflect?.errors ?? []) as string[];
      setDocStatus(`保存した（${json.label}）` + (errors.length > 0 ? `。プロヴィンスの反映に失敗 ${errors.length} 件` : ""));
      const f = featuresRef.current.get(docSlug);
      if (f && f.name !== json.label) {
        f.name = json.label;
        setVersion((v) => v + 1);
        requestOverlay();
      }
      if (!f) setUnplaced((list) => list.map((u) => (u.slug === docSlug ? { ...u, name: json.label } : u)));
    } catch (e) {
      setDocStatus(`失敗: ${(e as Error).message}`);
    }
  };

  // 地物モードを離れたら md のパネルを閉じる
  useEffect(() => {
    if (!active) setDocSlug(null);
  }, [active]);

  // ── 一覧 ──
  const filtered = useMemo(() => {
    void version;
    const q = query.trim();
    const ql = q.toLowerCase();
    const out: FeatureView[] = [];
    for (const f of featuresRef.current.values()) {
      if (category !== "all" && f.category !== category) continue;
      if (q && !f.name.includes(q) && !f.slug.includes(ql)) continue;
      out.push(f);
    }
    out.sort((a, b) => (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0));
    return out;
  }, [category, query, version]);

  const filteredUnplaced = useMemo(() => {
    const q = query.trim();
    const ql = q.toLowerCase();
    return unplaced.filter(
      (u) =>
        (category === "all" || u.category === category) && (!q || u.name.includes(q) || u.slug.includes(ql))
    );
  }, [category, query, unplaced]);

  const input: React.CSSProperties = {
    width: "100%",
    padding: "5px 8px",
    marginBottom: 6,
    fontSize: 13,
    background: "var(--bg-field)",
    color: "var(--text)",
    border: "1px solid var(--line)",
    borderRadius: 6,
  };
  const small: React.CSSProperties = { fontSize: 11, color: "var(--text-dim)" };
  const center: React.CSSProperties = { textAlign: "center" };
  const checkRow: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    margin: "0 0 8px",
    cursor: "pointer",
  };

  const selectedFeature = selected ? (featuresRef.current.get(selected) ?? null) : null;
  const draftTypes = draft ? types.filter((t) => t.category === draft.category) : [];
  const g = geomRef.current;
  const vertexCount = g.lines.reduce((n, line) => n + line.length, 0);

  // ── 表示の入/切 ──
  const toggles = (
    <>
      <button style={{ ...btn(showTown), marginTop: 6 }} onClick={() => setShowTown((v) => !v)}>
        街 {showTown ? "あり" : "なし"}
      </button>
      <button style={{ ...btn(showLandmark), marginTop: 6 }} onClick={() => setShowLandmark((v) => !v)}>
        ランドマーク {showLandmark ? "あり" : "なし"}
      </button>
      <button style={{ ...btn(showRoad), marginTop: 6 }} onClick={() => setShowRoad((v) => !v)}>
        街道 {showRoad ? "あり" : "なし"}
      </button>
      <button style={{ ...btn(showUndiscovered), marginTop: 6 }} onClick={() => setShowUndiscovered((v) => !v)}>
        未発見 {showUndiscovered ? "あり" : "なし"}
      </button>
    </>
  );

  // ── サイドバー ──
  const draftPanel = draft && (
    <div
      style={{
        marginTop: 10,
        padding: 8,
        border: "1px solid var(--accent)",
        borderRadius: 6,
        background: "rgba(200,162,74,0.06)",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, overflowWrap: "anywhere" }}>{draft.name || draft.slug}</div>
      <div style={small}>
        {draft.slug ? `${draft.slug} の形を直す` : draft.attach ? `${draft.newSlug} へ形を付ける` : "新しく作る"}
        　{CATEGORY_LABEL[draft.category]}・{SHAPE_LABEL[draft.shape]}
      </div>

      <div style={{ ...label, marginTop: 8 }}>種別</div>
      <select value={draft.type} onChange={(e) => changeDraftType(e.target.value)} style={input}>
        {draftTypes.map((t) => (
          <option key={t.type} value={t.type}>
            {t.label}（{SHAPE_LABEL[t.shape]}）
          </option>
        ))}
      </select>

      <label style={checkRow}>
        <input
          type="checkbox"
          checked={!draft.discovered}
          onChange={(e) => changeDraftDiscovered(!e.target.checked)}
        />
        未発見
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <button style={{ ...btn(tool === "edit"), ...center }} onClick={() => setTool("edit")}>
          形を置く
        </button>
        <button style={{ ...btn(tool === "move"), ...center }} onClick={() => setTool("move")}>
          動かす
        </button>
      </div>

      {draft.shape === "point" && (
        <p style={{ ...small, margin: "8px 0 0" }}>
          地図を押した場所へ置く。もう一度押すと置き直す
          <br />
          {g.pixels.length > 0 ? `x ${g.pixels[0][0]}　y ${g.pixels[0][1]}` : "まだ置いていない"}
        </p>
      )}

      {draft.shape === "line" && (
        <>
          <p style={{ ...small, margin: "8px 0 6px" }}>
            押すと頂点を足す。引きずると続けて足す
            <br />線 {g.lines.filter((line) => line.length > 0).length} 本　頂点 {vertexCount}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <button style={{ ...btn(false), ...center }} onClick={breakLine}>
              線を区切る
            </button>
            <button style={{ ...btn(false), ...center }} onClick={popVertex}>
              最後の頂点を消す
            </button>
          </div>
        </>
      )}

      {draft.shape === "area" && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 6 }}>
            <button style={{ ...btn(!eraser), ...center }} onClick={() => setEraser(false)}>
              筆
            </button>
            <button style={{ ...btn(eraser), ...center }} onClick={() => setEraser(true)}>
              消しゴム
            </button>
          </div>
          <div style={{ ...label, marginTop: 8 }}>筆の半径 {radius} px</div>
          <input
            type="range"
            min={0}
            max={40}
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value))}
            style={{ width: "100%" }}
          />
        </>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
        <button
          style={{ ...btn(false), ...center, opacity: canUndo ? 1 : 0.45 }}
          onClick={undo}
          disabled={!canUndo}
        >
          元に戻す
        </button>
        <button style={{ ...btn(false), ...center }} onClick={clearShape}>
          形を空にする
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
        <button
          style={{ ...btn(true), ...center, padding: "9px 8px", opacity: saving ? 0.45 : 1 }}
          onClick={saveDraft}
          disabled={saving}
        >
          保存
        </button>
        <button style={{ ...btn(false), ...center, padding: "9px 8px" }} onClick={cancelDraft}>
          やめる
        </button>
      </div>
    </div>
  );

  const selectedPanel = !draft && selectedFeature && (
    <div style={{ marginTop: 10, padding: 8, border: "1px solid var(--line)", borderRadius: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, overflowWrap: "anywhere" }}>
        {selectedFeature.name || selectedFeature.slug}
      </div>
      <div style={small}>
        {selectedFeature.slug}　{CATEGORY_LABEL[selectedFeature.category]}・
        {typeLabel(selectedFeature.category, selectedFeature.type)}・{SHAPE_LABEL[selectedFeature.shape]}
        {selectedFeature.discovered ? "" : "・未発見"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
        <button style={{ ...btn(false), ...center }} onClick={() => startEdit(selectedFeature)}>
          形を直す
        </button>
        <button style={{ ...btn(docSlug === selectedFeature.slug), ...center }} onClick={() => openDoc(selectedFeature.slug)}>
          文
        </button>
        <button style={{ ...btn(false), ...center }} onClick={() => jumpTo(selectedFeature)}>
          場所へ
        </button>
        <button
          style={{ ...btn(false), ...center, color: "#e07a6a" }}
          onClick={() => remove(selectedFeature.slug)}
        >
          削除
        </button>
      </div>
    </div>
  );

  const createPanel = !draft && creating && (
    <div style={{ marginTop: 10, padding: 8, border: "1px solid var(--line)", borderRadius: 6 }}>
      <div style={{ ...label, marginTop: 0 }}>表記名</div>
      <input value={newName} onChange={(e) => setNewName(e.target.value)} style={input} />
      <div style={label}>置き場</div>
      <select
        value={newCategory}
        onChange={(e) => {
          const cat = e.target.value as FeatureCategory;
          setNewCategory(cat);
          setNewType(types.find((t) => t.category === cat)?.type ?? "");
        }}
        style={input}
      >
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {CATEGORY_LABEL[c]}
          </option>
        ))}
      </select>
      <div style={label}>種別</div>
      <select value={newType} onChange={(e) => setNewType(e.target.value)} style={input}>
        {types
          .filter((t) => t.category === newCategory)
          .map((t) => (
            <option key={t.type} value={t.type}>
              {t.label}（{SHAPE_LABEL[t.shape]}）
            </option>
          ))}
      </select>
      <div style={label}>スラッグ（空なら連番）</div>
      <input
        value={newSlug}
        onChange={(e) => setNewSlug(e.target.value)}
        placeholder="例 river-0001"
        style={input}
        spellCheck={false}
        autoCapitalize="off"
      />
      <label style={checkRow}>
        <input type="checkbox" checked={newUndiscovered} onChange={(e) => setNewUndiscovered(e.target.checked)} />
        未発見
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <button style={{ ...btn(true), ...center }} onClick={startCreate}>
          形を置く
        </button>
        <button style={{ ...btn(false), ...center }} onClick={() => setCreating(false)}>
          閉じる
        </button>
      </div>
    </div>
  );

  const panel = (
    <>
      {draftPanel}
      {selectedPanel}
      {createPanel}

      {!draft && !creating && (
        <div style={{ marginTop: 10 }}>
          <button
            style={{ ...btn(false), ...center }}
            onClick={() => {
              setCreating(true);
              setSelected(null);
            }}
          >
            新規
          </button>
        </div>
      )}

      <div style={label}>種類</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
        <button style={{ ...btn(category === "all"), ...center }} onClick={() => setCategory("all")}>
          全て
        </button>
        {CATEGORIES.map((c) => (
          <button key={c} style={{ ...btn(category === c), ...center }} onClick={() => setCategory(c)}>
            {CATEGORY_LABEL[c]}
          </button>
        ))}
      </div>

      <div style={label}>
        地物（{filtered.length.toLocaleString()} 件{filtered.length > LIST_MAX ? `・先頭 ${LIST_MAX} 件を表示` : ""}）
      </div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="名前かスラッグで絞る"
        style={input}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 300, overflowY: "auto" }}>
        {filtered.slice(0, LIST_MAX).map((f) => (
          <button
            key={f.slug}
            style={{ ...btn(selected === f.slug), padding: "5px 8px" }}
            onClick={() => select(f.slug, true)}
            disabled={!!draft}
          >
            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.name || f.slug}
            </span>
            <span style={{ ...small, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {typeLabel(f.category, f.type)}　{f.slug}
            </span>
          </button>
        ))}
        {filtered.length === 0 && <span style={small}>当てはまる地物が無い</span>}
      </div>

      <div style={{ marginTop: 10 }}>
        <button style={btn(showUnplaced)} onClick={() => setShowUnplaced((v) => !v)}>
          形の無いフォルダ（{filteredUnplaced.length}）{showUnplaced ? "▲" : "▼"}
        </button>
      </div>
      {showUnplaced && (
        <div
          style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflowY: "auto", marginTop: 6 }}
        >
          {filteredUnplaced.slice(0, LIST_MAX).map((u) => (
            <button
              key={u.slug}
              style={{ ...btn(false), padding: "5px 8px" }}
              onClick={() => startAttach(u)}
              disabled={!!draft}
              title="このフォルダへ形を付ける"
            >
              <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {u.name}
              </span>
              <span style={{ ...small, display: "block" }}>
                {CATEGORY_LABEL[u.category]}　{u.slug}
              </span>
            </button>
          ))}
          <span style={small}>選ぶと、そのフォルダへ形を付ける</span>
        </div>
      )}
    </>
  );

  // ── md のパネル ──
  const docPanel = docSlug && (
    <div
      // 地図側がポインタを掴むため、この中の操作は親へ渡さない
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        bottom: 0,
        width: "min(560px, 60%)",
        background: "var(--bg-panel)",
        borderLeft: "1px solid var(--line)",
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <strong style={{ color: "var(--accent)", fontSize: 13 }}>{docSlug} の説明</strong>
        <div style={{ display: "flex", gap: 6 }}>
          <button style={{ ...btn(true), width: "auto", padding: "4px 12px" }} onClick={saveDoc}>
            保存
          </button>
          <button
            style={{ ...btn(false), width: "auto", padding: "4px 12px" }}
            onClick={() => {
              setDocSlug(null);
              setDocStatus("");
            }}
          >
            閉じる
          </button>
        </div>
      </div>
      <textarea
        value={docText}
        onChange={(e) => setDocText(e.target.value)}
        spellCheck={false}
        style={{
          flex: 1,
          minHeight: 0,
          resize: "none",
          padding: "10px 12px",
          background: "var(--bg)",
          color: "var(--text)",
          border: "none",
          outline: "none",
          fontFamily: "ui-monospace, Consolas, monospace",
          fontSize: 13,
          lineHeight: 1.7,
        }}
      />
      <div
        style={{
          padding: "6px 12px",
          borderTop: "1px solid var(--line)",
          fontSize: 11,
          color: "var(--text-dim)",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>1 行目の見出しを変えると地物の名前も変わる</span>
        <span>{docStatus}</span>
      </div>
    </div>
  );

  return {
    canvasRef,
    draw,
    panel,
    toggles,
    docPanel,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerLeave,
    namesNear,
    reload: load,
    /** 保存していない形を抱えているか */
    hasUnsaved: !!draft && dirty,
    /** 形を置いている最中か。カーソルの形を決めるのに使う */
    editing: !!draft && tool === "edit",
    discardDraft: endDraft,
  };
}
